import * as React from "react";
import { Button, Field, Text, makeStyles } from "@fluentui/react-components";
import type {
  ConditionDraft,
  ConditionGroupDraft,
  QueryEntityMetadata,
} from "../../../types.js";
import { FilteredComboBox } from "../../shared/FilteredCombobox.js";
import { getTargetMetadata } from "../fetchXmlBuilderHelpers.js";
import { ConditionEditor } from "./ConditionEditor.js";

const useStyles = makeStyles({
  conditionBox: {
    border: "1px solid var(--colorNeutralStroke2)",
    borderRadius: "6px",
    padding: "8px",
    display: "grid",
    gap: "8px",
  },
  row: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
    flexWrap: "wrap",
  },
});

export interface ConditionGroupTreeProps {
  group: ConditionGroupDraft;
  depth: number;
  conditionGroups: ConditionGroupDraft[];
  conditions: ConditionDraft[];
  rootMetadata: QueryEntityMetadata | null;
  joinMetadataByJoinId: Record<string, QueryEntityMetadata | undefined>;
  onAddCondition: (groupId: string) => void;
  onAddSubGroup: (groupId: string) => void;
  onRemoveGroup: (groupId: string) => void;
  onChangeGroup: (groupId: string, update: Partial<ConditionGroupDraft>) => void;
  onChangeCondition: (conditionId: string, update: Partial<ConditionDraft>) => void;
  onRemoveCondition: (conditionId: string) => void;
}

export function ConditionGroupTree({
  group,
  depth,
  conditionGroups,
  conditions,
  rootMetadata,
  joinMetadataByJoinId,
  onAddCondition,
  onAddSubGroup,
  onRemoveGroup,
  onChangeGroup,
  onChangeCondition,
  onRemoveCondition,
}: ConditionGroupTreeProps): JSX.Element {
  const styles = useStyles();
  const targetMetadata = getTargetMetadata(group.target, rootMetadata, joinMetadataByJoinId);
  const groupConditions = conditions.filter((item) => item.groupId === group.id);
  const childGroups = conditionGroups.filter((item) => item.parentGroupId === group.id);

  return (
    <div className={styles.conditionBox} style={{ marginLeft: `${depth * 16}px`, borderColor: "var(--colorNeutralStroke2)" }}>
      <div className={styles.row}>
        <Text weight="semibold">Group ({group.type.toUpperCase()})</Text>
        <Field label="Type" style={{ minWidth: 180 }}>
          <FilteredComboBox
            options={[
              ["and", "and"],
              ["or", "or"],
            ]}
            selectedOption={group.type}
            onValueChange={(value) => onChangeGroup(group.id, { type: value === "or" ? "or" : "and" })}
          />
        </Field>

        <Button appearance="secondary" onClick={() => onAddCondition(group.id)}>
          Add condition
        </Button>
        <Button appearance="secondary" onClick={() => onAddSubGroup(group.id)}>
          Add subgroup
        </Button>
        <Button appearance="subtle" onClick={() => onRemoveGroup(group.id)}>
          Remove group
        </Button>
      </div>

      {groupConditions.map((condition) => (
        <ConditionEditor
          key={condition.id}
          condition={condition}
          targetMetadata={targetMetadata}
          onChangeCondition={onChangeCondition}
          onRemoveCondition={onRemoveCondition}
        />
      ))}

      {childGroups.map((child) => (
        <ConditionGroupTree
          key={child.id}
          group={child}
          depth={depth + 1}
          conditionGroups={conditionGroups}
          conditions={conditions}
          rootMetadata={rootMetadata}
          joinMetadataByJoinId={joinMetadataByJoinId}
          onAddCondition={onAddCondition}
          onAddSubGroup={onAddSubGroup}
          onRemoveGroup={onRemoveGroup}
          onChangeGroup={onChangeGroup}
          onChangeCondition={onChangeCondition}
          onRemoveCondition={onRemoveCondition}
        />
      ))}
    </div>
  );
}
